/**
 * A series of "unit test" like assertions that will run locally and in prod to
 * determine health of the client side app.
 * Logs to sfx/fpti. Can log a more detailed scoreCard report (see ./sendScoreCard.ts)
 *
 * We have 3 flags to control if these checks run.
 * (see 'HealthCheckModeType' below):
 * 0. Off by default (mode zero)
 * 1. weasley app config (UCP) (severus/config) healthCheck.ENABLED flag
 * 1. Elmo factors from treatment
 * 2. window.PAYPAL.checkout.healthCheck.isDisabled
 */

import { getFactors } from '@/lib/features/utils';
import type { SendScoreCardProps } from './sendScoreCard';
import {
  generateSimpleScoreCardPayloadForFPTI,
  sendDetailedScoreCard,
} from './sendScoreCard';
import type { HealthCheckEventType, ScoreCardType } from './scoreCard';
import { generateHealthScore, THRESHOLD_FATAL } from './scoreCard';
import { getWeasleyAppConfig } from '@/lib/globals/globals';

// Constants are defined via app/ucp config in severus/config.json:weasleyAppConfig
const weasleyAppConfig = getWeasleyAppConfig();

// for now, we'll only allow one healthCheck
// We expect the react MOUNT to be the first healthcheck in a healthy scenario.
// If problems then the onload healthcheck should fire
let healthCheckHasRun = false;
const LIMIT_HEALTHECK_TO_SINGLE_RUN =
  weasleyAppConfig.healthCheck?.LIMIT_HEALTHECK_TO_SINGLE_RUN;

/**
 * 0 = OFF. Disable all healthchecks.
 * 1 = Soft checks. When health checks fail send FATAL metric to signalFx.
 * @DEPRECATED 2 = Hard checks. Throw on health check fail, and let window.onerror handle.
 * Levels are cumulative.
 */
type HealthCheckModeType = 0 | 1 | undefined;

// get the logLevel from elmo. Using factors allows us to set the `mode:0|1|2`
// key/val directly on the treatment.
const elmoMode: HealthCheckModeType | undefined | typeof NaN =
  getFactors('weasley_healthchecks')?.mode &&
  Number(getFactors('weasley_healthchecks')?.mode);

// elmo factors sends values as strings. Need to convert to valid number
const healthCheckModeFromElmo: HealthCheckModeType = Number.isInteger(elmoMode)
  ? (Number(elmoMode) as HealthCheckModeType)
  : undefined;

/**
 * runHealthCheck()
 * Runs a series of assertions that tell us if the app is healthy or not.
 * Feature flags from elmo deterimine whether healthchecks are OFF, log only,
 * or log then Throw when a fatal error is encountered (like blank page).
 * @param evt {string} Evt that caused healthCheck to be run
 * @param src {string} Filename src
 * @returns {string} exit reason so we can track in tests the early aborts work
 * */
export const runHealthCheck = function runHealthCheck(
  {
    event = '',
    eventSrc = '',
  }: { event: HealthCheckEventType; eventSrc: string } = {
    event: '',
    eventSrc: '',
  },
): string {
  const manualDisableFlag = window.PAYPAL.checkout?.healthCheck?.isDisabled
    ? 0
    : null;

  const weasleyCfgDisableFlag = weasleyAppConfig.healthCheck?.ENABLED
    ? null
    : 0;

  /**
   * Precedence for setting healthcheck mode:
   * 1. Check the dom for disabled flag (allows us to manually en/dis-able it
   *    for tests or manual testing)
   * 2. UCP healtCheck.ENABLED flag
   * 3. Elmo factor on experiment
   * 4. fallback to OFF
   */
  const mode: HealthCheckModeType =
    manualDisableFlag ?? weasleyCfgDisableFlag ?? healthCheckModeFromElmo ?? 0;

  // bail if mode == OFF OR if healthcheck runs limit has been met
  if (healthCheckHasRun) {
    return 'healthCheck run limit Met';
  }

  if (mode === 0 || !mode) {
    return 'mode=OFF (or node nullish)';
  }

  // if we want to limit to one run, mark as limit met
  if (LIMIT_HEALTHECK_TO_SINGLE_RUN) {
    healthCheckHasRun = true;
  }

  const rootEl = document.getElementById('root');

  const scoreCard = generateHealthScore(rootEl, event, eventSrc);

  // only include first failed assertion (assumed to be most severe)
  const firstFailureReason: string =
    (scoreCard.failedAssertions[0] as string) ?? 'n/a';

  // if this block enters we consider that an FCI
  // PROBLEM Detected
  if (scoreCard.score > THRESHOLD_FATAL) {
    const props: SendScoreCardProps = {
      event: event || '-1', // what event casued this to be called?
      level: 'fatal',
      reason: 'score_card.' + firstFailureReason,
      src: eventSrc || 'health-check/index.ts',
    };

    logMetricAndFPTI(props.level, scoreCard);
    sendDetailedScoreCard(props, scoreCard); // will log report when sampling is enabled via UCP

    // abort early
    return 'FAILING score. Send logs and abort';
  }

  // no healthchecks failed
  logMetricAndFPTI('success', scoreCard);

  //eslint-disable-next-line no-console
  console.debug('##Health-check -> scoreCard:', scoreCard);

  // TODO: Refactor and move all sending metric logic (sfx, cal, simple and detailed)
  // (and sendScoreCard.ts) to a logging.ts file
  const props: SendScoreCardProps = {
    event: event || '-1', // what event casued this to be called?
    level: 'success',
    reason: 'score_card.' + firstFailureReason,
    src: eventSrc || 'health-check/index.ts',
  };

  sendDetailedScoreCard(props, scoreCard); // will log report when sampling is enabled via UCP

  // for testing only :|. To verify early return when needed
  return 'PASSED | Full function run';
};

// runHealthCheck() needs to be attached on window to confirm positive registry
// low level + stable + reliable
window.PAYPAL ??= {};
window.PAYPAL.checkout ??= {};
window.PAYPAL.checkout.healthCheck ??= {
  runCheck: runHealthCheck,
  // sendScoreCard: sendDetailedScoreCard,
};

window.PAYPAL.checkout.healthCheck.runCheck = runHealthCheck;

// if healthcheck is set to OFF via Elmo or appConfig/UCP, set healthcheck as disabled.
if (
  healthCheckModeFromElmo === 0 ||
  weasleyAppConfig.healthCheck?.ENABLED === false
) {
  window.PAYPAL.checkout.healthCheck.isDisabled = true;
}

type outcomeLevel = SendScoreCardProps['level'];

/**
 * Sends a metric to signalFx with only a few values from the scoreCard
 * Also logs a simplified scoreCard to FPTI.
 * @param level: success|warning|fatal
 * (fatal means FCI/fallback/unrecoverable state)
 * @param reason: which checkpoint failed
 */
const logMetricAndFPTI = function (
  level: outcomeLevel,
  scoreCard: ScoreCardType,
): void {
  const firstFailureReason = scoreCard.failedAssertions[0] ?? 'n/a';
  const reason = 'score_card.' + firstFailureReason;

  const fptiPayload = generateSimpleScoreCardPayloadForFPTI(
    {
      event: scoreCard.event,
      level,
      reason,
      src: 'health-check/index:logMetricAndFPTI()',
    },
    scoreCard,
  );

  // send metric to signalFx
  const metricPayload = {
    metrics: [
      {
        dimensions: {
          client_app: 'weasley',
          level,
          reason,
        },
        metricEventName: 'n/a', // can't be blank or it'll fail in beaver-logger-paypal
        metricNamespace: 'pp.xo.ui.health_check.count',
        metricSrc: 'weasley',
        metricValue: 1,
        name: '',
      },
    ],
    // log to FPTI too
    tracking: [fptiPayload],
  };

  // using low-level logging mechanism in case beaver-logger client has any
  // problems
  window?.logPayload?.(metricPayload);
};
